import { create } from 'zustand'
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware'
import type { GardenZone, PlantGardenState, PlantState } from '@/types/plantTypes'
import { GARDEN_ZONES } from '@/types/plantTypes'
import { normalizePlantState } from '@/entities/plant-lifecycle'
import type {
  PlantGardenV2Snapshot,
  PlantNourishmentResultV2,
  PlantRewardResultV2,
} from '@/entities/plant-garden-v2'

export const LEGACY_PLANT_STORAGE_KEY = 'openelp-plant-garden-v1'
export const FOREST_TERRAIN_STORAGE_KEY = 'openelp-forest-terrain'
export const FOREST_LAYOUT_STORAGE_KEY = 'openelp-forest-layout-v1'
const STUDENT_PLANT_STORAGE_KEY = 'openelp-student-plant-gardens-v1'
const STUDENT_PLANT_V2_STORAGE_KEY = 'openelp-student-plant-garden-v2'
const isMysqlMode = import.meta.env?.VITE_OPENELP_WORKSPACE_MODE?.trim() === 'mysql'

const readOnlyStorage: StateStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
}

function studentStorage() {
  if (isMysqlMode) return createJSONStorage(() => readOnlyStorage)
  return typeof localStorage === 'undefined' ? undefined : createJSONStorage(() => localStorage)
}

function emptyPlant(): PlantState {
  return {
    species: null,
    exp: 0,
    growthExp: 0,
    stage: 'seedling',
    plantedAt: null,
    instanceId: null,
    nutrient: 0,
    lastNourishedAt: null,
  }
}

export function createEmptyPlantGardenState(): PlantGardenState {
  return {
    gardens: Object.fromEntries(GARDEN_ZONES.map((zone) => [zone, emptyPlant()])) as Record<GardenZone, PlantState>,
    album: {},
    history: [],
    processedRewardClaims: {},
    processedNourishmentEvents: {},
  }
}

export function normalizePlantGardenState(value: Partial<PlantGardenState> | null | undefined): PlantGardenState {
  return {
    gardens: Object.fromEntries(GARDEN_ZONES.map((zone) => [
      zone,
      normalizePlantState(value?.gardens?.[zone], zone),
    ])) as Record<GardenZone, PlantState>,
    album: value?.album ?? {},
    history: Array.isArray(value?.history) ? value.history : [],
    processedRewardClaims: value?.processedRewardClaims ?? {},
    processedNourishmentEvents: value?.processedNourishmentEvents ?? {},
  }
}

function normalizeStudentPlantAuthorityState(
  value: Partial<StudentPlantGardenAuthorityState> | null | undefined,
): StudentPlantGardenAuthorityState {
  return {
    gardensByStudent: Object.fromEntries(Object.entries(value?.gardensByStudent ?? {})
      .map(([studentId, garden]) => [studentId, normalizePlantGardenState(garden)])),
    revisionsByStudent: value?.revisionsByStudent ?? {},
    eventsByStudent: value?.eventsByStudent ?? {},
    sourcesByStudent: value?.sourcesByStudent ?? {},
  }
}

/** UI projection only. PlantGardenRuntime is the only publisher. */
export const usePlantGardenStore = create<PlantGardenState>(() => createEmptyPlantGardenState())

export interface StudentPlantGardenAuthorityState {
  gardensByStudent: Record<string, PlantGardenState>
  revisionsByStudent: Record<string, number>
  eventsByStudent: Record<string, Record<string, { sourceKey: string; payloadHash: string }>>
  sourcesByStudent: Record<string, Record<string, { eventId: string; payloadHash: string }>>
}

export const useStudentPlantGardenAuthorityStore = create<StudentPlantGardenAuthorityState>()(
  persist(
    () => ({
      gardensByStudent: {},
      revisionsByStudent: {},
      eventsByStudent: {},
      sourcesByStudent: {},
    }),
    {
      name: STUDENT_PLANT_STORAGE_KEY,
      version: 2,
      storage: studentStorage(),
      migrate: (persisted) => normalizeStudentPlantAuthorityState(
        persisted as Partial<StudentPlantGardenAuthorityState>,
      ),
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<StudentPlantGardenAuthorityState>),
        ...normalizeStudentPlantAuthorityState(persisted as Partial<StudentPlantGardenAuthorityState>),
      }),
    },
  ),
)

export interface BrowserPlantGardenV2Record {
  snapshot: PlantGardenV2Snapshot
  rewardReceipts: Record<string, PlantRewardResultV2>
  rewardCompletionClaims: Record<string, string>
  nourishmentReceipts: Record<string, PlantNourishmentResultV2>
  nourishmentQuestionReceipts: Record<string, PlantNourishmentResultV2>
}

interface StudentPlantGardenV2AuthorityState {
  recordsByStudent: Record<string, BrowserPlantGardenV2Record>
}

/** Browser demo authority only. MySQL mode makes this Adapter read-only in memory. */
export const useStudentPlantGardenV2AuthorityStore = create<StudentPlantGardenV2AuthorityState>()(
  persist(
    () => ({ recordsByStudent: {} }),
    {
      name: STUDENT_PLANT_V2_STORAGE_KEY,
      version: 1,
      storage: studentStorage(),
    },
  ),
)

/** Legacy global data remains unassigned until an explicit student import is approved. */
export function readLegacyPlantGardenImport(): string | null {
  if (typeof localStorage === 'undefined') return null
  return localStorage.getItem(LEGACY_PLANT_STORAGE_KEY)
}

export type PersistedForestTerrain = 'plain' | 'creek' | 'mountain'

export interface PersistedForestTreePosition {
  gx: number
  gy: number
}

export type PersistedForestTreeLayout = Record<string, PersistedForestTreePosition>

interface PersistedForestLayoutEnvelope {
  version: 1
  terrains: Partial<Record<PersistedForestTerrain, PersistedForestTreeLayout>>
}

export interface ForestLayoutStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function forestLayoutKey(studentId: string): string {
  return `${FOREST_LAYOUT_STORAGE_KEY}:${encodeURIComponent(studentId)}`
}

function normalizeForestTreeLayout(value: unknown): PersistedForestTreeLayout {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const layout: PersistedForestTreeLayout = {}
  for (const [treeId, position] of Object.entries(value)) {
    if (!position || typeof position !== 'object' || Array.isArray(position)) continue
    const gx = Number((position as Partial<PersistedForestTreePosition>).gx)
    const gy = Number((position as Partial<PersistedForestTreePosition>).gy)
    if (!Number.isInteger(gx) || !Number.isInteger(gy) || gx < 0 || gy < 0) continue
    layout[treeId] = { gx, gy }
  }
  return layout
}

function browserForestLayoutStorage(): ForestLayoutStorage | null {
  return typeof localStorage === 'undefined' ? null : localStorage
}

function readForestLayoutEnvelope(
  studentId: string,
  storage: ForestLayoutStorage | null,
): PersistedForestLayoutEnvelope {
  if (!storage || !studentId) return { version: 1, terrains: {} }
  try {
    const parsed = JSON.parse(storage.getItem(forestLayoutKey(studentId)) ?? 'null') as Partial<PersistedForestLayoutEnvelope> | null
    if (!parsed || parsed.version !== 1 || !parsed.terrains || typeof parsed.terrains !== 'object') {
      return { version: 1, terrains: {} }
    }
    return {
      version: 1,
      terrains: {
        plain: normalizeForestTreeLayout(parsed.terrains.plain),
        creek: normalizeForestTreeLayout(parsed.terrains.creek),
        mountain: normalizeForestTreeLayout(parsed.terrains.mountain),
      },
    }
  } catch {
    return { version: 1, terrains: {} }
  }
}

export function readForestTerrain(): PersistedForestTerrain {
  if (typeof localStorage === 'undefined') return 'plain'
  const value = localStorage.getItem(FOREST_TERRAIN_STORAGE_KEY)
  return value === 'creek' || value === 'mountain' ? value : 'plain'
}

export function writeForestTerrain(terrain: PersistedForestTerrain): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(FOREST_TERRAIN_STORAGE_KEY, terrain)
}

/** Cosmetic, student-scoped layout only; it never participates in learning or plant lifecycle authority. */
export function readForestTreeLayout(
  studentId: string,
  terrain: PersistedForestTerrain,
  storage: ForestLayoutStorage | null = browserForestLayoutStorage(),
): PersistedForestTreeLayout {
  return { ...(readForestLayoutEnvelope(studentId, storage).terrains[terrain] ?? {}) }
}

export function writeForestTreeLayout(
  studentId: string,
  terrain: PersistedForestTerrain,
  layout: PersistedForestTreeLayout,
  storage: ForestLayoutStorage | null = browserForestLayoutStorage(),
): void {
  if (!storage || !studentId) return
  const current = readForestLayoutEnvelope(studentId, storage)
  storage.setItem(forestLayoutKey(studentId), JSON.stringify({
    version: 1,
    terrains: { ...current.terrains, [terrain]: normalizeForestTreeLayout(layout) },
  } satisfies PersistedForestLayoutEnvelope))
}
